import { ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { cn } from "~/lib/utils";
import { ConfigVersion, formatVersionMeta } from "./config-view";

interface VersionHistoryMenuProps {
  versions: ConfigVersion[];
  currentVersionId: number | null;
  onSelectVersion: (versionId: number | null) => void;
}

export const VersionHistoryMenu = ({
  versions,
  currentVersionId,
  onSelectVersion,
}: VersionHistoryMenuProps) => {
  if (versions.length === 0) {
    return null;
  }

  const current =
    versions.find((version) => version.version_id === currentVersionId) ??
    versions.find((version) => version.isLatest) ??
    versions[0];
  const { isLatest } = current;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors",
            isLatest
              ? "border-green-200 bg-green-50 hover:bg-green-100"
              : "border-yellow-200 bg-yellow-50 hover:bg-yellow-100"
          )}
        >
          <span
            className={cn("size-2 rounded-full", isLatest ? "bg-green-500" : "bg-yellow-500")}
            aria-hidden="true"
          />
          <span className={cn("font-semibold", isLatest ? "text-green-700" : "text-yellow-800")}>
            {isLatest ? "Active" : "Older version"}
          </span>
          <span className="font-semibold text-foreground">Version {current.number}</span>
          <span className="text-muted-foreground">
            {formatVersionMeta(current.created_at)} · {current.created_by}
          </span>
          <ChevronDown className="size-4 text-muted-foreground" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        <DropdownMenuLabel className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Version history
        </DropdownMenuLabel>
        {versions.map((version) => (
          <DropdownMenuItem
            key={version.version_id}
            className={cn(
              "flex flex-col items-start gap-0.5",
              version.version_id === current.version_id && "bg-accent"
            )}
            onSelect={() => onSelectVersion(version.isLatest ? null : version.version_id)}
          >
            <span className="flex items-center gap-2 font-medium">
              Version {version.number}
              {version.isLatest && (
                <span className="text-xs font-normal text-muted-foreground">(latest)</span>
              )}
            </span>
            <span className="text-xs text-muted-foreground">
              {formatVersionMeta(version.created_at)} · {version.created_by}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
